import { sceneManager } from ".."
import { closeAllMenus } from "./Menus"
import { toggleRowState } from "./Tabs"
import { t } from "./Translate"

// Renders native-style menu items into a menu element.
// Each item: { label, shortcut, action, checked(), enabled(), pro } or "-" for a separator,
// or a slider: { type: "slider", label, min, max, step, value(), onInput(value) }.
export const renderMenuItems = (menu, items) => {
	menu.innerHTML = ""
	items.forEach((item) => {
		if (item === "-") {
			menu.appendChild(document.createElement("hr"))
			return
		}
		if (item.type == "slider") {
			const row = document.createElement("label")
			row.className = "menu-list-slider"
			row.innerHTML = `<span class="label">${t(item.label)}</span>`
			const input = document.createElement("input")
			input.type = "range"
			input.min = item.min
			input.max = item.max
			input.step = item.step
			input.value = item.value()
			input.oninput = () => item.onInput(Number(input.value))
			row.appendChild(input)
			menu.appendChild(row)
			return
		}
		const button = document.createElement("button")
		button.className = "menu-list-item"
		button.disabled = item.enabled ? !item.enabled() : false
		button.innerHTML = `
			<span class="check">${item.checked && item.checked() ? "✓" : ""}</span>
			<span class="label">${t(item.label)}${item.pro ? ` <span class="pro-badge">Pro</span>` : ""}</span>
			<span class="shortcut">${item.shortcut || ""}</span>`
		button.onclick = () => {
			// Close first, so actions that open a popup aren't immediately closed again
			closeAllMenus()
			item.action()
		}
		menu.appendChild(button)
	})
}

// Where the last click was, so the colour palette opens next to the menu item used
let lastPointer = { x: 0, y: 0 }

const PALETTE = [
	"#3a8ee6", "#59c2ff", "#59c2c8", "#64c195", "#a3d977", "#f5d76e", "#f58d74", "#ff796a",
	"#e66fb5", "#a98ce6", "#c69c6d", "#8d5a3b", "#ffffff", "#bbbbbb", "#666666", "#222222",
]

// A swatch palette, opened in the object menu where the last click was
const pickColor = (subject) => {
	const setColor = (color) => {
		subject.color = color
		subject.setMaterial()
	}
	const menu = document.querySelector("#object-menu")
	menu.innerHTML = ""
	const grid = document.createElement("div")
	grid.className = "color-palette"
	PALETTE.forEach((color) => {
		const swatch = document.createElement("button")
		swatch.className = "color-swatch"
		swatch.style.background = color
		swatch.title = color
		if (color == subject.color?.toLowerCase()) swatch.classList.add("is-current")
		swatch.onclick = () => {
			setColor(color)
			menu.classList.remove("is-active")
		}
		grid.appendChild(swatch)
	})
	menu.appendChild(grid)

	// Any other colour: a visible native input, clicked directly so it always opens
	const custom = document.createElement("label")
	custom.className = "menu-list-item color-custom"
	custom.innerHTML = `<span class="check"></span><span class="label">${t("Custom…")}</span>`
	const input = document.createElement("input")
	input.type = "color"
	input.value = subject.color
	input.oninput = () => setColor(input.value)
	custom.appendChild(input)
	menu.appendChild(custom)

	showMenuAt(menu, lastPointer.x, lastPointer.y)
}

// Actions for one object, shared by its context menu and the Edit menu.
// With no subject, the same items come back disabled.
export const objectActions = (subject, { focus = false } = {}) => {
	const isCharacter = subject?.subjectType == "Character"
	const isProp = subject?.subjectType == "Prop"
	const enabled = () => !!subject
	const hidden = subject && !subject.object.visible
	const locked = subject && (isCharacter ? subject.jointsHidden : subject.locked)

	const items = []
	if (isCharacter && subject.isAdjustable) {
		items.push({ label: "Adjust proportions", pro: true, action: () => subject.toggleProportionsPanel() })
	}
	if (subject) {
		items.push({ label: "Color…", action: () => pickColor(subject) })
	}
	if (isProp) {
		items.push({
			label: subject.attachedTo ? "Detach from character" : "Attach to character",
			action: () => subject.onSecondaryTap(),
		})
	}
	if (items.length) items.push("-")

	items.push(
		{ label: "Duplicate", enabled, action: () => subject.duplicate() },
		{ label: "Flip", shortcut: "M", enabled, action: () => subject.flip() },
		{ label: "Place on ground", shortcut: "G", enabled, action: () => subject.putOnGround() },
	)
	if (focus) {
		items.push({ label: "Focus", shortcut: "F", enabled, action: () => sceneManager.setRotationPoint(subject) })
	}
	items.push(
		"-",
		{ label: hidden ? "Show" : "Hide", shortcut: "H", enabled, action: () => toggleRowState(subject, "hide") },
		{ label: locked ? "Unlock" : "Lock", shortcut: "K", enabled, action: () => toggleRowState(subject, "lock") },
		"-",
		{ label: "Delete", shortcut: "⌫", enabled, action: () => subject.delete() },
	)
	return items
}

// Context menu for an object, opened at a point (right-click, or the row's "…" button)
export const openObjectMenu = (subject, x, y) => {
	if (sceneManager.selectedManager.subject !== subject) {
		if (subject.subjectType == "Character") subject.selectCharacter()
		else subject.onTap()
	}
	closeAllMenus()

	const menu = document.querySelector("#object-menu")
	renderMenuItems(menu, objectActions(subject, { focus: true }))
	showMenuAt(menu, x, y)
}

const showMenuAt = (menu, x, y) => {
	menu.style.left = `${x}px`
	menu.style.top = `${y}px`
	menu.classList.add("is-active")

	// Keep it on screen: shift left, or open upwards
	const rect = menu.getBoundingClientRect()
	if (rect.right > innerWidth - 8) menu.style.left = `${Math.max(8, innerWidth - rect.width - 8)}px`
	if (rect.bottom > innerHeight - 8) menu.style.top = `${Math.max(8, y - rect.height)}px`
}

// Like any context menu, a press anywhere else dismisses it
document.addEventListener("pointerdown", (event) => {
	lastPointer = { x: event.clientX, y: event.clientY }
	const menu = document.querySelector("#object-menu")
	if (menu && menu.classList.contains("is-active") && !menu.contains(event.target) && !event.target.closest(".row-more")) {
		menu.classList.remove("is-active")
	}
}, true)
